import { useCallback, useRef } from 'react';
import styled from 'styled-components';

const Needle = styled.div`
    position: absolute;
    top: 0;
    width: 2px;
    height: 100%;
    background: #e53935;
    pointer-events: none;
    z-index: 20;
    transform: translateX(-50%);

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 50%;
        transform: translateX(-50%);
        width: 0;
        height: 0;
        border-left: 6px solid transparent;
        border-right: 6px solid transparent;
        border-top: 8px solid #e53935;
    }
`;

/**
 * Playhead — a red vertical needle that can be dragged.
 *
 * Props:
 *   time        — current playhead time in OUTPUT seconds
 *   zoomLevel   — px/sec
 *   scrollLeft  — horizontal scroll of the timeline (px)
 *   duration    — output duration in seconds
 *   onSeek(t)   — callback when user drags/clicks to a new output time
 */
export default function Playhead({ time, zoomLevel, scrollLeft, duration, onSeek }) {
    const xPx = time * zoomLevel - scrollLeft;

    return <Needle style={{ left: `${xPx}px` }} />;
}

/**
 * useTimelineScrub — attaches pointer-down → move → up to allow scrubbing.
 *
 * Call this on the timeline container div ref.
 * @param {function} onSeekOriginal  — receives time in ORIGINAL timeline seconds
 * @param {number} zoomLevel
 * @param {number} scrollLeft
 * @param {function} originalToOutputTime  — from editorUtils
 * @param {number[]} cuts
 */
export function useTimelineScrub({ containerRef, onSeek, zoomLevel, scrollLeft, leftOffset = 0 }) {
    const dragging = useRef(false);
    // Store last pointerId so we can release capture on up
    const lastPointerId = useRef(null);

    const getTimeFromEvent = useCallback(
        (e) => {
            const rect = containerRef.current?.getBoundingClientRect();
            if (!rect) return 0;
            // Allow caller to pass a left offset (label column) that should be ignored
            const x = e.clientX - rect.left - leftOffset + scrollLeft;
            return Math.max(0, x / zoomLevel);
        },
        [containerRef, zoomLevel, scrollLeft, leftOffset],
    );

    const onPointerDown = useCallback(
        (e) => {
            dragging.current = true;
            lastPointerId.current = e.pointerId;
            try {
                containerRef.current?.setPointerCapture(e.pointerId);
            } catch (err) {
                // ignore if capture isn't available
            }
            // Prevent default to avoid accidental text selection/dragging
            if (e.cancelable) e.preventDefault();
            onSeek(getTimeFromEvent(e));
        },
        [containerRef, getTimeFromEvent, onSeek],
    );

    const onPointerMove = useCallback(
        (e) => {
            if (!dragging.current) return;
            onSeek(getTimeFromEvent(e));
        },
        [getTimeFromEvent, onSeek],
    );

    const onPointerUp = useCallback(
        (e) => {
            dragging.current = false;
            // release pointer capture if possible
            try {
                if (containerRef.current && (e?.pointerId || lastPointerId.current)) {
                    containerRef.current.releasePointerCapture(e?.pointerId || lastPointerId.current);
                }
            } catch (err) {
                // ignore
            }
            lastPointerId.current = null;
        },
        [containerRef],
    );

    return { onPointerDown, onPointerMove, onPointerUp };
}
